import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import type { DeckV2 } from '@/types/slide';

/** Names of tools the backend agent can call.  Matches TOOL_HANDLERS in
 *  backend/providers/services/presentation_tools.py (plus a synthetic
 *  "thinking" step for the agent's reasoning text). */
export type AgentToolName =
  | 'thinking'
  | 'edit_slide'
  | 'replace_slide'
  | 'add_slide'
  | 'delete_slide'
  | 'duplicate_slide'
  | 'move_slide'
  | 'change_theme'
  | 'regenerate_image'
  | 'set_image'
  | 'apply_image_effect'
  | 'edit_cost_table'
  | 'edit_block'
  | 'ask_user'
  | 'finish'
  | 'cannot_do'
  | 'system';

export interface AgentStep {
  tool: AgentToolName;
  input: Record<string, unknown>;
  result: string;
  ok: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  changedSlides?: number[];
  /** True when the AI changed deck-level theme properties rather than individual slides. */
  isThemeChange?: boolean;
  /** Agent transcript — one entry per tool call, in execution order. */
  transcript?: AgentStep[];
  /** Clarifying question when the agent called `ask_user` — requires a reply. */
  pendingQuestion?: string | null;
  /** Clickable option chips the user can pick as a quick reply. */
  pendingQuestionOptions?: string[];
  /** Images attached to a user message. */
  attachedImages?: Array<{ url: string; name: string }>;
}

export interface ChatRequest {
  message: string;
  current_slide_index: number;
  /** Optional image URLs uploaded by the user to include in the slide. */
  image_urls?: string[];
  /** Prior chat turns so the agent has conversation memory.  Each entry
   *  is ``{role: 'user'|'assistant', content: string}``; the current
   *  user message should NOT be included — backend appends it. */
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export interface ChatResponse {
  deck_json: DeckV2;
  /** New GCS URL for the rebuilt PPTX — present when the backend regenerated
   *  the file from deck_json.  Used to invalidate the ONLYOFFICE config so the
   *  editor reloads the fresh document in place. */
  pptx_url?: string | null;
  /** Sequence of agent steps (thinking + tool calls) for the Claude Code-style UI. */
  transcript: AgentStep[];
  /** Short user-facing summary of what the agent did. */
  summary: string;
  /** Non-null when the agent called `ask_user` — the UI should surface this
   *  as a clarifying question instead of showing the summary. */
  pending_question: string | null;
  /** Clickable options for the clarifying question (0-6 short strings). */
  pending_question_options?: string[];
  /** Indices of all slides that were modified (may be empty for pure questions). */
  affected_slides?: number[];
  /** Indices of slides whose images were regenerated. */
  regenerated_images?: number[];
  /** Number of agent iterations the loop used (max 8). Handy for debugging. */
  iterations?: number;
}

export const usePresentationChat = (presentationId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ChatResponse, Error, ChatRequest>({
    mutationFn: async (data) => {
      const res = await api.post(`/api/providers/presentations/${presentationId}/chat/`, data);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['presentation-detail', presentationId],
        (old: Record<string, unknown> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            deck_json: data.deck_json,
            ...(data.pptx_url ? { pptx_url: data.pptx_url } : {}),
          };
        },
      );
      // Only reload ONLYOFFICE when real slide changes happened.
      // Clarifying questions and no-op replies must NOT churn the
      // editor — the user was complaining about the iframe blanking
      // on every message.
      const hasChanges = (data.affected_slides?.length ?? 0) > 0;
      if (hasChanges) {
        queryClient.invalidateQueries({
          queryKey: ['onlyoffice-config', presentationId],
        });
      }
    },
  });
};
